import { useEffect, useState, useRef } from "react";
import { notificationAPI, userAPI, meetingAPI, documentAPI, announcementAPI } from "../services/api";
import { useAuth } from "../context/AuthContext";
import { Modal, Btn, Th, Td, Toast, Can, TableScroll, Pagination } from "../components/shared/index";
import { useLanguage } from "../context/LanguageContext";
import {
  MdSearch, MdWarning, MdSend, MdVisibility, MdDoneAll, MdNotificationsNone,
  MdEventNote, MdDescription, MdCampaign, MdInfo, MdClose,
} from "react-icons/md";

const BROWN  = "var(--brown)";
const BORDER = "var(--border)";
const BG     = "var(--cream)";
const MUTED  = "var(--muted)";
const KH     = "'Khmer OS Siemreap','Noto Sans Khmer',sans-serif";
const MO     = "'Moul','Noto Sans Khmer',serif";

const TYPES = [
  { code:"MEETING",      label:"កិច្ចប្រជុំ", icon:<MdEventNote size={16} />   },
  { code:"DOCUMENT",     label:"ឯកសារ",       icon:<MdDescription size={16} /> },
  { code:"ANNOUNCEMENT", label:"ការប្រកាស",   icon:<MdCampaign size={16} />    },
  { code:"SYSTEM",       label:"ប្រព័ន្ធ",    icon:<MdInfo size={16} />        },
];
const typeMeta = (code) => TYPES.find(t => t.code === code) || { label:code || "—", icon:<MdInfo size={16} /> };

const TYPE_NAV = {
  MEETING:      "meeting-list",
  DOCUMENT:     "doc-officers",
  ANNOUNCEMENT: "ann-list",
};

const REF_FIELD_LABEL = {
  meetingId:       "កិច្ចប្រជុំ",
  roomName:        "បន្ទប់",
  organizerName:   "អ្នករៀបចំ",
  senderName:      "អ្នកផ្ញើ",
  createdByName:   "បង្កើតដោយ",
  departmentName:  "នាយកដ្ឋាន",
  documentName:    "ឯកសារ",
  officerName:     "មន្ត្រី",
};

const inputStyle = {
  width:"100%", padding:"9px 12px",
  border:`1px solid ${BORDER}`, borderRadius:8,
  fontSize:13, outline:"none", background:"var(--surface)",
  fontFamily:KH, boxSizing:"border-box",
};
const errStyle = (key, errors) => errors[key]
  ? { ...inputStyle, border:"1.5px solid #EF4444", background:"#FFF5F5" }
  : inputStyle;

const toDateTime = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    d.setHours(d.getHours() + 7);
    return d.toLocaleString("en-GB");
  }
  catch { return iso; }
};

function ErrMsg({ field, errors }) {
  if (!errors[field]) return null;
  return (
    <p style={{ fontSize:11, color:"#EF4444", marginTop:3, fontFamily:KH, display:"flex", alignItems:"center", gap:3 }}>
      <MdWarning size={13} /> {errors[field]}
    </p>
  );
}

function Label({ children, required }) {
  return (
    <label style={{ display:"block", fontSize:12, color:MUTED,
                    marginBottom:4, fontFamily:KH }}>
      {children}
      {required && <span style={{ color:"#EF4444", marginLeft:3 }}>*</span>}
    </label>
  );
}

function SectionTitle({ children }) {
  return (
    <p style={{ fontSize:11, color:MUTED, fontWeight:600,
                margin:"14px 0 8px", borderBottom:`1px solid ${BORDER}`,
                paddingBottom:4, fontFamily:KH }}>
      {children}
    </p>
  );
}

function InfoRow({ label, value }) {
  return (
    <div style={{ display:"flex", padding:"9px 0",
                  borderBottom:`1px solid ${BORDER}` }}>
      <span style={{ fontSize:12, color:MUTED, minWidth:140,
                     fontFamily:KH, flexShrink:0 }}>{label}</span>
      <span style={{ fontSize:13, color:"var(--text)", fontFamily:KH }}>{value ?? "—"}</span>
    </div>
  );
}

function SkeletonRow({ cols }) {
  return (
    <tr>
      {Array(cols).fill(0).map((_,i) => (
        <td key={i} style={{ padding:"12px 16px", borderBottom:`1px solid ${BORDER}` }}>
          <div style={{ height:14, background:"var(--border)", borderRadius:4 }} />
        </td>
      ))}
    </tr>
  );
}

function RefPicker({ options, loading, value, onChange, typeLabel }) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [q,    setQ]    = useState("");
  const boxRef = useRef(null);

  useEffect(() => {
    const close = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const selected = options.find(o => String(o.id) === String(value));
  const filtered = options.filter(o => {
    if (!q) return true;
    const s = q.toLowerCase();
    return o.label.toLowerCase().includes(s) || String(o.id).includes(s);
  });

  return (
    <div ref={boxRef} style={{ position:"relative" }}>
      <input
        value={open ? q : (selected ? selected.label : "")}
        onFocus={() => { setOpen(true); setQ(""); }}
        onChange={e => { setQ(e.target.value); setOpen(true); }}
        placeholder={loading ? "កំពុងផ្ទុក..." : `ស្វែងរក${typeLabel}...`}
        disabled={loading}
        style={inputStyle}
      />
      {selected && !open && (
        <button type="button" onClick={() => onChange("")}
          title={t("លុបជម្រើស")}
          style={{ position:"absolute", right:8, top:"50%", transform:"translateY(-50%)",
                   border:"none", background:"none", cursor:"pointer",
                   color:MUTED, lineHeight:1, padding:2,
                   display:"flex", alignItems:"center" }}>
          <MdClose size={14}/>
        </button>
      )}
      {open && !loading && (
        <div style={{ position:"absolute", top:"100%", left:0, right:0, zIndex:30,
                      background:"var(--surface)", border:`1px solid ${BORDER}`, borderRadius:8,
                      marginTop:4, maxHeight:200, overflowY:"auto",
                      boxShadow:"0 4px 12px rgba(0,0,0,0.08)" }}>
          {filtered.length === 0 ? (
            <p style={{ padding:"12px", margin:0, textAlign:"center",
                        color:MUTED, fontSize:12, fontFamily:KH }}>
              {t("គ្មានលទ្ធផល")}
            </p>
          ) : filtered.map(o => (
            <div key={o.id}
              onClick={() => { onChange(String(o.id)); setOpen(false); setQ(""); }}
              style={{ padding:"8px 12px", cursor:"pointer", fontSize:13, fontFamily:KH,
                       color:"var(--text)", borderBottom:`1px solid ${BORDER}`,
                       background: String(o.id) === String(value) ? BG : "var(--surface)" }}
              onMouseEnter={e => e.currentTarget.style.background = BG}
              onMouseLeave={e => e.currentTarget.style.background =
                String(o.id) === String(value) ? BG : "var(--surface)"}>
              {o.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TypeBadge({ type }) {
  const { t } = useLanguage();
  const m = typeMeta(type);
  return (
    <span style={{ display:"inline-flex", alignItems:"center", gap:5,
                   padding:"3px 10px", borderRadius:20, fontSize:11,
                   fontWeight:600, background:BG, color:BROWN, fontFamily:KH }}>
      {m.icon} {t(m.label)}
    </span>
  );
}

export default function Notifications({ onNavigate }) {
  const { t } = useLanguage();
  const { user } = useAuth();
  const userId = user?.id;

  const [list,       setList]       = useState([]);
  const [users,      setUsers]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState("");
  const [readFil,    setReadFil]    = useState("");
  const [modal,      setModal]      = useState(null);
  const [selected,   setSelected]   = useState(null);
  const [form,       setForm]       = useState({ type:"SYSTEM" });
  const [recipients, setRecipients] = useState([]);
  const [recSearch,  setRecSearch]  = useState("");
  const [saving,     setSaving]     = useState(false);
  const [errors,     setErrors]     = useState({});
  const [page,       setPage]       = useState(0);
  const [pageSize,   setPageSize]   = useState(25);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [toast,      setToast]      = useState(null);
  const [refOptions, setRefOptions] = useState([]);
  const [refLoading, setRefLoading] = useState(false);

  const titleRef = useRef(null);
  const refCache = useRef({});

  const load = async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const res  = await notificationAPI.getMy(userId, page, pageSize,
        readFil === "" ? undefined : readFil === "true");
      const data = res?.data?.data ?? res?.data;
      setList(data?.content || []);
      setTotalPages(data?.totalPages    || 1);
      setTotalItems(data?.totalElements || 0);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, readFil, userId]);

  useEffect(() => {
    userAPI.getAll(0, 300).then(r => {
      const d = r?.data?.data ?? r?.data;
      const arr = (d?.content || []).map(u => ({
        userId:     u.userId ?? u.id,
        userNameKh: u.userNameKh,
        userNameEn: u.userNameEn,
        email:      u.email,
      }));
      setUsers(arr);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (modal !== "create") return;
    const notifType = form.type || "SYSTEM";
    if (notifType === "SYSTEM") { setRefOptions([]); return; }
    if (refCache.current[notifType]) { setRefOptions(refCache.current[notifType]); return; }
    const fetchers = {
      MEETING:      () => meetingAPI.getAll(0, 200),
      DOCUMENT:     () => documentAPI.getAll(0, 200),
      ANNOUNCEMENT: () => announcementAPI.getAll(0, 200),
    };
    setRefLoading(true);
    fetchers[notifType]()
      .then(r => {
        const d = r?.data?.data ?? r?.data;
        const arr = (d?.content || []).map(x => {
          const id = x.meetingId ?? x.documentId ?? x.announcementId ?? x.id;
          const label = x.title || x.documentName || `#${id}`;
          return { id, label };
        }).filter(o => o.id != null);
        refCache.current[notifType] = arr;
        setRefOptions(arr);
      })
      .catch(() => setRefOptions([]))
      .finally(() => setRefLoading(false));
  }, [modal, form.type]);

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const getNid = (n) => n.notificationId ?? n.id;

  const openCreate = () => {
    setForm({ type:"SYSTEM" });
    setRecipients([]); setRecSearch(""); setErrors({});
    setModal("create");
  };

  const toggleRecipient = (id) =>
    setRecipients(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  // The sender always gets a copy of their own (see handleSend), so offering
  // themselves as a recipient would deliver the notification to them twice.
  const selectableUsers = users.filter(u => u.userId !== userId);

  const filteredUsers = selectableUsers.filter(u => {
    if (!recSearch) return true;
    const q = recSearch.toLowerCase();
    return [u.userNameKh, u.userNameEn, u.email, String(u.userId)]
      .filter(Boolean).some(v => v.toLowerCase().includes(q));
  });
  const allShownSelected = filteredUsers.length > 0 &&
    filteredUsers.every(u => recipients.includes(u.userId));
  const toggleSelectAll = () => {
    if (allShownSelected) {
      const shown = new Set(filteredUsers.map(u => u.userId));
      setRecipients(prev => prev.filter(id => !shown.has(id)));
    } else {
      setRecipients(prev => [...new Set([...prev, ...filteredUsers.map(u => u.userId)])]);
    }
  };

  const handleSend = async () => {
    const reqErrors = {};
    if (recipients.length === 0) reqErrors.recipients = "សូមជ្រើសរើសអ្នកទទួលយ៉ាងតិចម្នាក់";
    if (!form.title?.trim())     reqErrors.title      = "សូមបញ្ចូលចំណងជើង";
    if (!form.message?.trim())   reqErrors.message    = "សូមបញ្ចូលសារ";
    if (Object.keys(reqErrors).length) {
      setErrors(reqErrors);
      if (reqErrors.title && titleRef.current) setTimeout(() => titleRef.current.focus(), 80);
      return;
    }

    setSaving(true);
    try {
      const base = {
        title:       form.title,
        message:     form.message,
        type:        form.type || "SYSTEM",
        referenceId: form.referenceId ? parseInt(form.referenceId, 10) : null,
      };
      // never address the sender here — they get their own copy below
      const targets = recipients.filter(id => id !== userId);
      if (targets.length === 1) {
        await notificationAPI.create({ ...base, userId: targets[0] });
      } else if (targets.length > 1) {
        await notificationAPI.createBulk({ ...base, userIds: targets });
      }
      if (userId) {
        await notificationAPI.create({ ...base, userId });
      }
      await load();
      setToast({ message:`បានផ្ញើការជូនដំណឹងទៅ ${targets.length} នាក់!`, type:"success" });
      setModal(null); setForm({ type:"SYSTEM" }); setRecipients([]); setErrors({});
    } catch (e) {
      try {
        const b = e.body || JSON.parse(e.message);
        if (b?.data && typeof b.data === "object") { setErrors(b.data); return; }
        setToast({ message: b?.message || "មានបញ្ហា សូមព្យាយាមម្ដងទៀត", type:"error" });
        return;
      } catch {}
      setToast({ message: e.message || "មានបញ្ហា", type:"error" });
    } finally {
      setSaving(false);
    }
  };

  const openView = async (n) => {
    setSelected(n); setModal("view");
    // the announcement keeps its own read state, separate from this notification
    if (n.type === "ANNOUNCEMENT" && n.referenceId) {
      announcementAPI.markAsRead(n.referenceId).catch(() => {});
    }
    if (!n.isRead) {
      try {
        await notificationAPI.markAsRead(getNid(n));
        setList(prev => prev.map(x => getNid(x) === getNid(n) ? { ...x, isRead:true } : x));
      } catch {}
    }
  };

  const handleReadAll = async () => {
    try {
      await notificationAPI.markAllRead(userId);
      setList(prev => prev.map(x => ({ ...x, isRead:true })));
      setToast({ message:t("បានសម្គាល់ថាអានទាំងអស់!"), type:"success" });
    } catch (e) {
      setToast({ message:t("មានបញ្ហា"), type:"error" });
    }
  };

  const filtered = list.filter(n => {
    if (!search) return true;
    const q = search.toLowerCase();
    return [n.title, n.message].filter(Boolean).some(v => v.toLowerCase().includes(q));
  });
  const unreadCount = list.filter(n => !n.isRead).length;

  return (
    <div>
      <Toast message={toast?.message} type={toast?.type} onHide={() => setToast(null)} />

      <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:12, marginBottom:20 }}>
        {[
          { key:"",      label:"ការជូនដំណឹងសរុប", value:totalItems,                    color:BROWN     },
          { key:"false", label:"មិនទាន់ឃើញ",       value:unreadCount,                   color:"#EF4444" },
          { key:"true",  label:"បានឃើញ",           value:list.length - unreadCount,     color:"#10B981" },
        ].map((s,i) => (
          <div key={i}
            onClick={() => { setReadFil(s.key); setPage(0); }}
            style={{ background:"var(--surface)", borderRadius:10, cursor:"pointer",
                     padding:"14px 18px", border:`1px solid ${BORDER}`,
                     boxShadow: readFil===s.key ? `0 0 0 2px ${s.color}` : "none",
                     transition:"box-shadow 0.15s" }}>
            <p style={{ margin:"0 0 6px", fontSize:12, color:MUTED, fontFamily:KH,
                        display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              {s.label}
              {readFil===s.key && (
                <span style={{ fontSize:10, color:s.color, fontWeight:600 }}>{t("កំពុងបង្ហាញ")}</span>
              )}
            </p>
            <p style={{ margin:0, fontSize:26, fontWeight:700, color:s.color, fontFamily:MO }}>
              {loading ? "..." : s.value}
            </p>
          </div>
        ))}
      </div>

      <div style={{ display:"flex", gap:12, marginBottom:16, alignItems:"center", flexWrap:"wrap" }}>
        <div style={{ flex:1, minWidth:200, display:"flex", alignItems:"center", gap:8,
                      background:"var(--surface)", border:`1px solid ${BORDER}`,
                      borderRadius:8, padding:"0 12px" }}>
          <MdSearch size={16} style={{ color:MUTED, flexShrink:0 }} />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder={t("ស្វែងរកចំណងជើង ឬ សារ...")}
            style={{ border:"none", outline:"none", background:"transparent",
                     flex:1, padding:"9px 0", fontSize:13, fontFamily:KH }} />
        </div>
        {unreadCount > 0 && (
          <button onClick={handleReadAll}
            style={{ padding:"9px 14px", background:"var(--surface)", color:BROWN,
                     border:`1px solid ${BORDER}`, borderRadius:8, cursor:"pointer",
                     fontSize:13, fontFamily:KH, display:"flex", alignItems:"center", gap:5 }}>
            <MdDoneAll size={15} /> {t("អានទាំងអស់")}
          </button>
        )}
        <Can perm="notification" action="create">
        <button onClick={openCreate}
          style={{ padding:"9px 18px", background:BROWN, color:"#fff",
                   border:"none", borderRadius:8, cursor:"pointer",
                   fontSize:13, fontWeight:500, whiteSpace:"nowrap", fontFamily:KH,
                   display:"flex", alignItems:"center", gap:5 }}>
          <MdSend size={15} /> {t("បង្កើត និងផ្ញើ")}
        </button>
        </Can>
      </div>

      <div style={{ background:"var(--surface)", borderRadius:10, border:`1px solid ${BORDER}`, overflow:"hidden" }}>
        <TableScroll>
          <thead>
            <tr>
              <Th>{t("ចំណងជើង")}</Th>
              <Th>{t("សារ")}</Th>
              <Th>{t("ប្រភេទ")}</Th>
              <Th>{t("ស្ថានភាព")}</Th>
              <Th>{t("ថ្ងៃ")}</Th>
              <Th>{t("សកម្មភាព")}</Th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              [1,2,3,4].map(i => <SkeletonRow key={i} cols={6} />)
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ padding:"36px", textAlign:"center", color:MUTED, fontSize:13, fontFamily:KH }}>
                  <MdNotificationsNone size={32} style={{ color:BORDER }} />
                  <p style={{ marginTop:6 }}>{t("គ្មានការជូនដំណឹង")}</p>
                </td>
              </tr>
            ) : filtered.map(n => (
              <tr key={getNid(n)}
                style={{ background: n.isRead ? "var(--surface)" : "var(--cream)" }}
                onMouseEnter={e => e.currentTarget.style.background = BG}
                onMouseLeave={e => e.currentTarget.style.background = n.isRead ? "var(--surface)" : "var(--cream)"}>
                <Td>
                  <span style={{ display:"flex", alignItems:"center", gap:6 }}>
                    {!n.isRead && <span style={{ width:8, height:8, borderRadius:"50%", background:"#EF4444", flexShrink:0 }} />}
                    <span style={{ fontWeight: n.isRead ? 500 : 700 }}>{n.title || "—"}</span>
                  </span>
                </Td>
                <Td muted>{(n.message || "—").slice(0, 50)}{(n.message || "").length > 50 ? "…" : ""}</Td>
                <Td><TypeBadge type={n.type} /></Td>
                <Td muted>{n.isRead ? "បានឃើញ" : "មិនទាន់ឃើញ"}</Td>
                <Td muted>{toDateTime(n.createdAt)}</Td>
                <td style={{ padding:"12px 16px", borderBottom:`1px solid ${BORDER}` }}>
                  <button onClick={() => openView(n)} title={t("មើលលម្អិត")}
                    style={{ background:"none", border:"none", cursor:"pointer",
                             color:"#3B82F6", display:"flex", alignItems:"center", padding:"4px" }}>
                    <MdVisibility size={18} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </TableScroll>

        <Pagination
          page={page} totalPages={totalPages} totalItems={totalItems} pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={n => { setPageSize(n); setPage(0); }}
        />
      </div>

      {modal === "create" && (
        <Modal
          title={t("បង្កើតការជូនដំណឹង")}
          subtitle={t("ផ្ញើទៅអ្នកប្រើដែលបានជ្រើសរើស")}
          onClose={() => { setModal(null); setErrors({}); }}
        >
          <SectionTitle>{t("ព័ត៌មានសារ")}</SectionTitle>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 16px" }}>
            <div style={{ marginBottom:14 }}>
              <Label required>{t("ប្រភេទ")}</Label>
              <select value={form.type||"SYSTEM"}
                onChange={e => setForm(p => ({ ...p, type:e.target.value, referenceId:"" }))}
                style={inputStyle}>
                {TYPES.map(ty => <option key={ty.code} value={ty.code}>{t(ty.label)}</option>)}
              </select>
            </div>
            <div style={{ marginBottom:14 }}>
              <Label>{t("លេខយោង (referenceId)")}</Label>
              {(form.type||"SYSTEM") === "SYSTEM" ? (
                <select value="" disabled style={{ ...inputStyle, background:"#F5F5F5", color:MUTED }}>
                  <option value="">{t("— មិនចាំបាច់ —")}</option>
                </select>
              ) : (
                <RefPicker
                  options={refOptions}
                  loading={refLoading}
                  value={form.referenceId||""}
                  onChange={v => f("referenceId", v)}
                  typeLabel={t(typeMeta(form.type).label)}
                />
              )}
            </div>
          </div>
          <div style={{ marginBottom:14 }}>
            <Label required>{t("ចំណងជើង")}</Label>
            <input ref={titleRef} value={form.title||""}
              onChange={e => { f("title", e.target.value); setErrors(p=>({...p,title:undefined})); }}
              placeholder={t("ចំណងជើងការជូនដំណឹង")} style={errStyle("title", errors)} />
            <ErrMsg field="title" errors={errors} />
          </div>
          <div style={{ marginBottom:14 }}>
            <Label required>{t("សារ")}</Label>
            <textarea value={form.message||""}
              onChange={e => { f("message", e.target.value); setErrors(p=>({...p,message:undefined})); }}
              rows={3} placeholder={t("ខ្លឹមសារ...")}
              style={{ ...errStyle("message", errors), resize:"vertical" }} />
            <ErrMsg field="message" errors={errors} />
          </div>

          <SectionTitle>អ្នកទទួល ({recipients.length} នាក់)</SectionTitle>
          <ErrMsg field="recipients" errors={errors} />
          <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8 }}>
            <div style={{ flex:1, display:"flex", alignItems:"center", gap:6,
                          background:"var(--surface)", border:`1px solid ${BORDER}`,
                          borderRadius:8, padding:"0 10px" }}>
              <MdSearch size={15} style={{ color:MUTED }} />
              <input value={recSearch} onChange={e => setRecSearch(e.target.value)}
                placeholder={t("ស្វែងរកអ្នកប្រើ...")}
                style={{ border:"none", outline:"none", background:"transparent",
                         flex:1, padding:"8px 0", fontSize:13, fontFamily:KH }} />
            </div>
            <button onClick={toggleSelectAll}
              style={{ padding:"8px 12px", background:BG, color:BROWN,
                       border:`1px solid ${BORDER}`, borderRadius:8, cursor:"pointer",
                       fontSize:12, fontFamily:KH, whiteSpace:"nowrap" }}>
              {allShownSelected ? "ដកចេញ" : "ជ្រើសទាំងអស់"}
            </button>
          </div>
          <div style={{ maxHeight:180, overflowY:"auto", border:`1px solid ${BORDER}`,
                        borderRadius:8 }}>
            {filteredUsers.length === 0 ? (
              <p style={{ padding:"18px", textAlign:"center", color:MUTED, fontSize:13, fontFamily:KH }}>
                {t("គ្មានអ្នកប្រើ")}
              </p>
            ) : filteredUsers.map(u => (
              <label key={u.userId}
                style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 12px",
                         borderBottom:`1px solid ${BORDER}`, cursor:"pointer", fontFamily:KH,
                         background: recipients.includes(u.userId) ? BG : "var(--surface)" }}>
                <input type="checkbox" checked={recipients.includes(u.userId)}
                  onChange={() => toggleRecipient(u.userId)} />
                <span style={{ fontSize:13, color:"var(--text)" }}>
                  {u.userNameKh || u.userNameEn || `ID: ${u.userId}`}
                  {u.email ? <span style={{ color:MUTED, fontSize:11 }}> · {u.email}</span> : null}
                </span>
              </label>
            ))}
          </div>

          <p style={{ fontSize:11, color:MUTED, fontFamily:KH, marginTop:10,
                      display:"flex", alignItems:"center", gap:5 }}>
            <MdInfo size={14} style={{ flexShrink:0 }}/> {t("អ្នកនឹងទទួលច្បាប់ចម្លងមួយ ដើម្បីមើលឃើញក្នុងបញ្ជីរបស់អ្នក។")}
          </p>

          <div style={{ display:"flex", justifyContent:"flex-end", gap:10, marginTop:16 }}>
            <Btn variant="outline" onClick={() => { setModal(null); setErrors({}); }}>{t("បោះបង់")}</Btn>
            <Btn onClick={handleSend} style={{ opacity:saving ? 0.7 : 1 }}>
              {saving ? "កំពុងផ្ញើ..." : <span style={{display:"flex",alignItems:"center",gap:5}}><MdSend size={14}/> {t("ផ្ញើ")}</span>}
            </Btn>
          </div>
        </Modal>
      )}

      {modal === "view" && selected && (() => {
        const refEntries = Object.entries(selected.referenceData || {})
          .filter(([, v]) => v !== null && v !== undefined && v !== "");
        const navKey = TYPE_NAV[selected.type];
        return (
          <Modal
            title={selected.title || "ការជូនដំណឹង"}
            subtitle={<TypeBadge type={selected.type} />}
            onClose={() => setModal(null)}
          >
            <SectionTitle>{t("ខ្លឹមសារ")}</SectionTitle>
            <p style={{ fontSize:13, color:"var(--text)", fontFamily:KH,
                        lineHeight:1.7, whiteSpace:"pre-wrap" }}>
              {selected.message || "—"}
            </p>

            <SectionTitle>{t("ព័ត៌មានលម្អិត")}</SectionTitle>
            <InfoRow label={t("លេខសម្គាល់")}  value={getNid(selected)} />
            <InfoRow label={t("ប្រភេទ")}       value={t(typeMeta(selected.type).label)} />
            <InfoRow label={t("ស្ថានភាព")}     value={selected.isRead ? t("បានឃើញ") : t("មិនទាន់ឃើញ")} />
            <InfoRow label={t("លេខយោង")}       value={selected.referenceId} />
            <InfoRow label={t("ថ្ងៃផ្ញើ")}      value={toDateTime(selected.createdAt)} />
            <InfoRow label={t("ថ្ងៃអាន")}       value={toDateTime(selected.readAt)} />
            {refEntries.map(([k, v]) => (
              <InfoRow key={k} label={t(REF_FIELD_LABEL[k] || k)} value={String(v)} />
            ))}

            <div style={{ marginTop:20, display:"flex", justifyContent:"space-between" }}>
              {navKey && onNavigate ? (
                <Btn onClick={() => { onNavigate(navKey); setModal(null); }}>
                  {t("មើលលម្អិត")}
                </Btn>
              ) : <span />}
              <Btn variant="outline" onClick={() => setModal(null)}>{t("បិទ")}</Btn>
            </div>
          </Modal>
        );
      })()}
    </div>
  );
}
